<template>
  <b-modal
    v-model="localShow"
    :title="isEditMode ? $t('Webportal.Actions.InquiryWizard.Attachment.EditTitle') : $t('Webportal.Actions.InquiryWizard.Attachment.AddTitle')"
    size="lg"
    @hidden="handleModalHidden"
    @ok="handleModalOk"
  >
    <b-form @submit.prevent="handleModalOk">
      <b-alert v-if="!isEditMode && attachments.length > 0" show :variant="validFilesCount >= 1 ? 'info' : 'warning'" class="mb-3">
        <font-awesome-icon :icon="['fal', 'info-circle']" class="mr-2" />
        <span v-if="validFilesCount >= 1">
          {{ $t('Webportal.Actions.InquiryWizard.Attachment.ValidFilesInfo', { count: validFilesCount }) }}
        </span>
        <span v-else>
          {{ $t('Webportal.Actions.InquiryWizard.Attachment.NoValidFiles') }}
        </span>
      </b-alert>

      <div class="row">
        <div class="col-md-6">
          <b-form-group label-for="document-type">
            <template #label> {{ $t('Webportal.Actions.InquiryWizard.Attachment.DocumentType') }} <span class="text-danger">*</span> </template>
            <b-form-select id="document-type" v-model="attachmentMetadata.documentType" :options="documentTypeOptions" required />
          </b-form-group>
        </div>
        <div class="col-md-6">
          <b-form-group label-for="document-date">
            <template #label> {{ $t('Webportal.Actions.InquiryWizard.Attachment.DocumentDate') }} <span class="text-danger">*</span> </template>
            <b-form-datepicker
              id="document-date"
              v-model="attachmentMetadata.documentDate"
              :locale="datepickerLocale"
              v-bind="datepickerLabels"
              :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
              :max="new Date()"
              required
            />
          </b-form-group>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <b-form-group label-for="document-scope">
            <template #label> {{ $t('Webportal.Actions.InquiryWizard.Attachment.Scope') }} <span class="text-danger">*</span> </template>
            <b-form-select id="document-scope" v-model="attachmentMetadata.scope" :options="scopeOptions" required />
          </b-form-group>
        </div>
        <div class="col-md-6">
          <b-form-group
            :label="$t('Webportal.Actions.InquiryWizard.Attachment.ShortDescription')"
            label-for="document-description"
            :description="$t('Webportal.Actions.InquiryWizard.Attachment.ShortDescriptionHint')"
          >
            <b-form-input id="document-description" v-model="attachmentMetadata.shortDescription" :placeholder="$t('Webportal.Actions.InquiryWizard.Attachment.ShortDescriptionPlaceholder')" />
          </b-form-group>
        </div>
      </div>

      <!-- File info for edit mode -->
      <b-form-group v-if="isEditMode && attachments.length > 0" :label="$t('Webportal.Actions.InquiryWizard.Attachment.FileLabel')">
        <div class="file-preview">
          <div class="d-flex align-items-center">
            <font-awesome-icon :icon="['fal', 'file']" class="mr-2" size="2x" />
            <div>
              <div class="font-weight-bold">{{ attachments[0].file.name }}</div>
              <div class="small text-muted">{{ formatFileSize(attachments[0].file.size) }}</div>
            </div>
          </div>
        </div>
      </b-form-group>

      <!-- Selected files for upload mode -->
      <b-form-group v-if="!isEditMode && attachments.length > 0" :label="$t('Webportal.Actions.InquiryWizard.Attachment.SelectedFiles')">
        <div v-for="(file, index) in attachments" :key="index" class="file-preview mb-2">
          <div class="d-flex align-items-center">
            <font-awesome-icon :icon="['fal', 'file']" class="mr-2" />
            <div class="flex-grow-1">
              <div class="small font-weight-bold">{{ file.name }}</div>
              <div class="small text-muted">{{ formatFileSize(file.size) }}</div>
            </div>
            <div v-if="getFileValidationError(file)" class="text-danger small">
              {{ getFileValidationError(file) }}
            </div>
          </div>
        </div>
      </b-form-group>
    </b-form>

    <template #modal-footer="{ ok, cancel }">
      <b-button variant="secondary" @click="cancel()">{{ $t('Webportal.Cancel') }}</b-button>
      <b-button variant="primary" :disabled="!canSaveAttachments" @click="ok()"> {{ $t('Webportal.Save') }} </b-button>
    </template>
  </b-modal>
</template>

<script>
import SurveyRevisionMixin from "@/mixins/SurveyRevisionMixin";
import DatepickerMixin from "@/mixins/DatepickerMixin";

export default {
  name: "AttachmentMetadataDialog",

  mixins: [SurveyRevisionMixin, DatepickerMixin],

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    attachments: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      localShow: false,
      attachmentMetadata: {
        documentType: null,
        documentDate: null,
        scope: "question",
        shortDescription: "",
      },
    };
  },

  watch: {
    show(val) {
      this.localShow = val;
      if (val) {
        this.initForm();
      }
    },
  },

  computed: {
    isEditMode() {
      return this.attachments.length === 1 && this.attachments[0].documentType !== undefined;
    },

    canSaveAttachments() {
      if (this.isEditMode) {
        return this.attachmentMetadata.documentType && this.attachmentMetadata.documentDate && this.attachmentMetadata.scope;
      }
      return (
        this.attachments.length > 0 &&
        this.attachmentMetadata.documentType &&
        this.attachmentMetadata.documentDate &&
        this.attachmentMetadata.scope &&
        this.hasAtLeastOneValidFile
      );
    },

    hasAtLeastOneValidFile() {
      return this.attachments.some((file) => this.isFileObject(file) && !this.getFileValidationError(file));
    },

    validFilesCount() {
      return this.attachments.filter((file) => this.isFileObject(file) && !this.getFileValidationError(file)).length;
    },
  },

  methods: {
    initForm() {
      if (this.isEditMode && this.attachments.length > 0) {
        const attachment = this.attachments[0];
        this.attachmentMetadata = {
          documentType: attachment.documentType,
          documentDate: attachment.documentDate,
          scope: attachment.scope,
          shortDescription: attachment.shortDescription || "",
        };
      } else {
        this.resetMetadata();
      }
    },

    isFileObject(file) {
      return file && file.name !== undefined;
    },

    getFileValidationError(file) {
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        return this.$t('Webportal.Actions.InquiryWizard.Attachment.TooLarge');
      }

      const allowedExtensions = [".pdf", ".jpg", ".jpeg", ".png", ".mp4", ".doc", ".docx", ".xlsx", ".xls"];
      const fileName = file.name.toLowerCase();
      const isAllowed = allowedExtensions.some((ext) => fileName.endsWith(ext));

      if (!isAllowed) {
        return this.$t('Webportal.Actions.InquiryWizard.Attachment.InvalidType');
      }

      return null;
    },

    handleModalOk() {
      if (!this.canSaveAttachments) return;

      if (this.isEditMode) {
        const updatedAttachment = {
          ...this.attachments[0],
          documentType: this.attachmentMetadata.documentType,
          documentDate: this.attachmentMetadata.documentDate,
          scope: this.attachmentMetadata.scope,
          shortDescription: this.attachmentMetadata.shortDescription,
        };
        this.$emit("attachment-updated", updatedAttachment);
      } else {
        const validFiles = this.attachments.filter((file) => this.isFileObject(file) && !this.getFileValidationError(file));

        if (validFiles.length === 0) {
          this.$bvToast.toast(this.$t('Webportal.Actions.InquiryWizard.Attachment.NoValidFilesToUpload'), {
            title: this.$t('Webportal.Warning'),
            variant: "warning",
          });
          return;
        }

        const attachments = validFiles.map((file) => ({
          file: file,
          documentType: this.attachmentMetadata.documentType,
          documentDate: this.attachmentMetadata.documentDate,
          scope: this.attachmentMetadata.scope,
          shortDescription: this.attachmentMetadata.shortDescription,
        }));

        this.$emit("attachments-added", attachments);
      }

      this.localShow = false;
    },

    handleModalHidden() {
      this.resetMetadata();
      this.$emit("hidden");
    },

    resetMetadata() {
      this.attachmentMetadata = {
        documentType: null,
        documentDate: null,
        scope: "question",
        shortDescription: "",
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.file-preview {
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 0.75rem;
  background-color: #f8f9fa;
}
</style>
